import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApiError, api, apiBlob } from "../lib/api.ts";
import { useApi, formatDate, relativeTime, titleCase } from "../lib/hooks.ts";
import { useAuth } from "../lib/auth.tsx";
import type {
  DeviceDetail, RequestType, ProcessesResponse,
  ProgramsResponse, UpdatesResponse, BlocklistResponse, BackupsResponse,
  LicensesResponse, HealthResponse, BiosDump, BiosDumpsResponse,
  ActionRequest, InstalledProgram, WindowsUpdate,
} from "../lib/types.ts";
import { Badge, Card, Empty, ErrorAlert, Modal, ProgressBar, Spinner } from "../components/ui.tsx";
import { BiosReferences } from "../components/BiosReferences.tsx";
import { DeviceLocationCard } from "../components/DeviceLocationCard.tsx";

interface ActionDef {
  type: RequestType;
  label: string;
  danger?: boolean;
  description: string;
}

const ACTIONS: ActionDef[] = [
  { type: "freeze", label: "Freeze", description: "Bloqueia o uso do dispositivo ate liberacao remota." },
  { type: "reboot", label: "Reiniciar computador", danger: true, description: "Reinicia o endpoint remotamente. O agente envia o relatorio ao painel antes de reiniciar." },
  { type: "wipe", label: "Wipe", danger: true, description: "Apaga perfis, arquivos e dados do endpoint (irreversivel)." },
  { type: "file-delete", label: "Excluir arquivo", danger: true, description: "Remove um arquivo especifico pela rota informada." },
  { type: "message", label: "Enviar mensagem", description: "Exibe aviso na tela do usuario final." },
  { type: "script", label: "Executar script", description: "Roda um script da biblioteca Reach no endpoint." },
  { type: "inventory-programs", label: "Inventario de programas", description: "Coleta lista de programas instalados." },
  { type: "inventory-updates", label: "Verificar atualizacoes", description: "Consulta atualizacoes pendentes do Windows." },
  { type: "install-updates", label: "Instalar atualizacoes", description: "Instala atualizacoes pendentes." },
  { type: "inventory-processes", label: "Processos ativos", description: "Lista processos em execucao." },
  { type: "backup", label: "Backup de seguranca", description: "Copia documentos e registro." },
  { type: "bios_backup", label: "Backup de BIOS", description: "Le a ROM da placa-mae via driver (somente leitura) e envia ao console." },
  { type: "self-update", label: "Atualizar agente", description: "Verifica a versao publicada e atualiza o agente neste computador agora (reinicio silencioso do processo, sem janelas)." },
  { type: "esp-boot-install", label: "Persistencia de boot (ESP)", description: "Instala um shim na ESP que roda antes do Windows (nao altera o firmware). Exige Secure Boot desativado." },
  { type: "esp-boot-remove", label: "Remover persistencia de boot", description: "Restaura o Boot Manager original e remove o shim da ESP." },
  { type: "logo-install", label: "Aplicar logo do boot", description: "Instala a logo da empresa no boot do Windows (HackBGRT) e no splash da ESP. Exige Secure Boot desativado." },
  { type: "recovery-install", label: "Provisionar particao de recovery", description: "Cria a particao RECOVERY com o mini-SO usando o pendrive plugado (SecureEndpoint\\Setup-Recovery.ps1 + payload\\boot.wim). Leva alguns minutos; acompanhe em 'Status da recovery'." },
  { type: "recovery-status", label: "Status da recovery", description: "Mostra as particoes, a entrada na NVRAM e o log do provisionamento da particao RECOVERY." },
  { type: "wipe-status", label: "Status do wipe", description: "Mostra o andamento do ultimo wipe (log wipe.log) e se o dispositivo esta marcado como apagado." },
];

interface RecoveryTokenResponse {
  token: string;
  deviceUid: string;
  expiresAt: string;
}

function versionAtLeast(version: string, minimum: string): boolean {
  const a = version.split(".").map((part) => parseInt(part, 10));
  const b = minimum.split(".").map((part) => parseInt(part, 10));
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const left = a[i] ?? 0;
    const right = b[i] ?? 0;
    if (Number.isNaN(left) || Number.isNaN(right)) return false;
    if (left !== right) return left > right;
  }
  return true;
}

const RECOVERY_TOKEN_TIMEOUT_MS = 15000;

// O endpoint administrativo registra o token no evento de auditoria do
// dispositivo; a interface nunca exibe esse valor na linha do tempo.
function maskRecoveryToken(message: string): string {
  return message.replace(/(Token de recuperacao criado:\s*)([0-9A-Fa-f]+)/g, "$1[oculto]");
}

function licenseBadge(status: string): { value: string; label: string } {
  switch (status) {
    case "ativado":
      return { value: "completed", label: "Ativado" };
    case "instalado":
      return { value: "info", label: "Instalado" };
    case "digital":
      return { value: "info", label: "Licenca digital" };
    case "nao-licenciado":
    case "nao-genuino":
      return { value: "critical", label: titleCase(status) };
    case "notificacao":
      return { value: "warning", label: "Notificacao" };
    default:
      if (status.startsWith("graca")) return { value: "warning", label: titleCase(status) };
      return { value: "unknown", label: status || "â€”" };
  }
}

// Inventario completo coletado pelo agente a cada checkin (raw_json).
// Mostra CPU/RAM/discos/GPU e TODOS os adaptadores de rede com MAC/IP.
function InventarioHardware({ raw }: { raw: Record<string, unknown> | undefined }) {
  const inv = (raw ?? {}) as {
    hardware?: {
      cpuName?: string; cpuCores?: number; cpuThreads?: number; ramGb?: number;
      storageTotalGb?: number;
      memory?: {
        totalGb?: number; slotsUsed?: number; slotsTotal?: number | null; speedMax?: number;
        modules?: Array<{ slot?: string; capacityGb?: number; speedMHz?: number; partNumber?: string; manufacturer?: string; serial?: string }>;
      };
      disks?: Array<{ model?: string; sizeGb?: number; interface?: string; serial?: string }>;
      gpus?: string[];
    };
    network?: {
      ip?: string; mac?: string;
      adapters?: Array<{ name: string; description?: string; mac?: string; status?: string; linkSpeed?: string; ip?: string }>;
    };
  };
  const hw = inv.hardware;
  const mem = hw?.memory;
  const memModules = mem?.modules ?? [];
  const adapters = inv.network?.adapters ?? [];
  const hasData = Boolean(hw?.cpuName || hw?.ramGb || (hw?.disks?.length ?? 0) > 0 || adapters.length > 0 || mem);
  if (!hasData) return null;

  return (
    <Card title="Inventario de hardware">
      <div className="grid cols-2" style={{ alignItems: "start" }}>
        <section className="panel">
          <div className="panel-title">Processador e memoria</div>
          <dl className="kv">
            <dt>CPU</dt>
            <dd>{hw?.cpuName || "â€”"}</dd>
            <dt>Nucleos / threads</dt>
            <dd>{hw?.cpuCores != null ? `${hw.cpuCores}C/${hw.cpuThreads ?? "?"}T` : "â€”"}</dd>
            <dt>Memoria RAM</dt>
            <dd>{hw?.ramGb != null ? `${hw.ramGb} GB` : "â€”"}</dd>
            <dt>Slots de memoria</dt>
            <dd>
              {mem
                ? mem.slotsTotal
                  ? `${mem.slotsUsed ?? memModules.length}/${mem.slotsTotal} preenchidos`
                  : `${mem.slotsUsed ?? memModules.length} modulo(s)`
                : "â€”"}
            </dd>
            <dt>Velocidade da memoria</dt>
            <dd>{mem?.speedMax ? `${mem.speedMax} MHz` : "â€”"}</dd>
            <dt>Armazenamento</dt>
            <dd>{hw?.storageTotalGb != null ? `${hw.storageTotalGb} GB` : "â€”"}</dd>
            <dt>Placa de video</dt>
            <dd>{hw?.gpus?.length ? hw.gpus.join(" Â· ") : "â€”"}</dd>
          </dl>
        </section>

        <section className="panel">
          <div className="panel-title">Rede</div>
          <dl className="kv">
            <dt>IP principal</dt>
            <dd className="mono">{inv.network?.ip || "â€”"}</dd>
            <dt>MAC principal</dt>
            <dd className="mono">{inv.network?.mac || "â€”"}</dd>
            <dt>Adaptadores</dt>
            <dd>{adapters.length > 0 ? `${adapters.length} encontrado(s)` : "â€”"}</dd>
          </dl>
        </section>
      </div>

      {memModules.length > 0 ? (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr><th>Slot</th><th>Tamanho</th><th>Velocidade</th><th>Fabricante</th><th>Part number</th><th>Serie</th></tr>
            </thead>
            <tbody>
              {memModules.map((m, i) => (
                <tr key={i}>
                  <td><div className="cell-title">{m.slot || `Slot ${i + 1}`}</div></td>
                  <td className="cell-sub">{m.capacityGb != null ? `${m.capacityGb} GB` : "â€”"}</td>
                  <td className="cell-sub">{m.speedMHz ? `${m.speedMHz} MHz` : "â€”"}</td>
                  <td className="cell-sub">{m.manufacturer || "â€”"}</td>
                  <td className="mono cell-sub">{m.partNumber || "â€”"}</td>
                  <td className="mono cell-sub">{m.serial || "â€”"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {(hw?.disks?.length ?? 0) > 0 ? (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr><th>Disco</th><th>Tamanho</th><th>Interface</th><th>Serie</th></tr>
            </thead>
            <tbody>
              {(hw?.disks ?? []).map((d, i) => (
                <tr key={i}>
                  <td><div className="cell-title">{d.model || "Disco"}</div></td>
                  <td className="cell-sub">{d.sizeGb != null ? `${d.sizeGb} GB` : "â€”"}</td>
                  <td className="cell-sub">{d.interface || "â€”"}</td>
                  <td className="mono cell-sub">{d.serial || "â€”"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {adapters.length > 0 ? (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr><th>Adaptador</th><th>Descricao</th><th>MAC</th><th>IP</th><th>Status</th><th>Velocidade</th></tr>
            </thead>
            <tbody>
              {adapters.map((a, i) => (
                <tr key={i}>
                  <td><div className="cell-title">{a.name}</div></td>
                  <td className="cell-sub">{a.description || "â€”"}</td>
                  <td className="mono cell-sub">{a.mac || "â€”"}</td>
                  <td className="mono cell-sub">{a.ip || "â€”"}</td>
                  <td>
                    <Badge
                      value={a.status === "Up" ? "completed" : a.status === "Disconnected" ? "warning" : "unknown"}
                      label={a.status || "â€”"}
                    />
                  </td>
                  <td className="cell-sub">{a.linkSpeed || "â€”"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

export function DeviceDetailPage() {
  const { uid = "" } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const { data, error, loading, reload } = useApi<DeviceDetail>(
    uid ? `/api/v3/devices/${uid}` : null,
    [],
    { poll: 5000 },
  );
  const [active, setActive] = useState<ActionDef | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Tarefas longas: instalar updates e desinstalar programa rodam em segundo
  // plano no endpoint; a UI acompanha pelo uid da requisicao.
  const [installReq, setInstallReq] = useState<ActionRequest | null>(null);
  const [installBusy, setInstallBusy] = useState(false);
  const [uninstallReq, setUninstallReq] = useState<ActionRequest | null>(null);
  const [uninstallTarget, setUninstallTarget] = useState<InstalledProgram | null>(null);
  const [uninstallBusy, setUninstallBusy] = useState(false);
  const [programBusy, setProgramBusy] = useState<string | null>(null);

  const [freezeHours, setFreezeHours] = useState("4");
  const [freezeMessage, setFreezeMessage] = useState("Contate o suporte para liberacao do dispositivo.");
  const [wipeScope, setWipeScope] = useState("data");
  const [wipeConfirm, setWipeConfirm] = useState(false);
  const [filePath, setFilePath] = useState("C:/Users/Public/documento.docx");
  const [messageBody, setMessageBody] = useState("Seu dispositivo passara por manutencao hoje.");
  const [scriptName, setScriptName] = useState("coletar-inventario");
  const [blockPattern, setBlockPattern] = useState("");
  const [blockAction, setBlockAction] = useState<"block" | "unblock">("block");
  const [backupType, setBackupType] = useState("quick");
  const [rebootDelay, setRebootDelay] = useState(60);

  // Estados das novas secoes
  const [showPrograms, setShowPrograms] = useState(false);
  const [showUpdates, setShowUpdates] = useState(false);
  const [showBlocklist, setShowBlocklist] = useState(false);
  const [showBackups, setShowBackups] = useState(false);
  const [showBios, setShowBios] = useState(false);
  const [biosBusy, setBiosBusy] = useState(false);
  const [allowTestMode, setAllowTestMode] = useState(false);

  // â”€â”€ Token de recuperacao (endpoint administrativo existente) â”€â”€
  const [recPhase, setRecPhase] = useState<"idle" | "confirm" | "generating" | "done" | "error">("idle");
  const [recToken, setRecToken] = useState<RecoveryTokenResponse | null>(null);
  const [recError, setRecError] = useState<string | null>(null);
  const [recAmbiguous, setRecAmbiguous] = useState(false);
  const [recCopied, setRecCopied] = useState(false);

  const { data: programs, loading: progLoading, reload: reloadPrograms } = useApi<ProgramsResponse>(
    showPrograms && uid ? `/api/v3/devices/${uid}/programs` : null,
    [],
    { poll: 6000 },
  );
  const { data: updates, loading: updLoading, reload: reloadUpdates } = useApi<UpdatesResponse>(
    showUpdates && uid ? `/api/v3/devices/${uid}/updates` : null,
    [],
    { poll: 6000 },
  );
  const { data: blocklist, loading: blLoading, reload: reloadBlocklist } = useApi<BlocklistResponse>(
    (showBlocklist || showPrograms) && uid ? `/api/v3/devices/${uid}/blocklist` : null,
    [],
    { poll: 6000 },
  );
  const { data: backups, loading: bkLoading, reload: reloadBackups } = useApi<BackupsResponse>(
    showBackups && uid ? `/api/v3/devices/${uid}/backups` : null,
  );
  const { data: biosDumps, loading: biosLoading, reload: reloadBios } = useApi<BiosDumpsResponse>(
    showBios && uid ? `/api/v3/devices/${uid}/bios-dumps` : null,
  );

  const JOB_ACTIVE = ["pending", "running", "deferred"];
  const isJobActive = (r: ActionRequest | null) => !!r && JOB_ACTIVE.includes(r.status);
  const jobResult = (r: ActionRequest | null) =>
    (r?.result ?? {}) as {
      progress?: number;
      phase?: string;
      current?: number;
      total?: number;
      indeterminate?: boolean;
      output?: string;
      rebootRequired?: boolean;
    };
  const jobPhaseLabel = (phase?: string) => {
    switch (phase) {
      case "pesquisando": return "Consultando atualizacoes";
      case "baixando": return "Baixando atualizacoes";
      case "instalando": return "Instalando atualizacoes";
      case "localizando": return "Localizando programa";
      case "desinstalando": return "Desinstalando programa";
      case "iniciando": return "Iniciando";
      case "concluido": return "Concluido";
      case "erro": return "Falhou";
      default: return "Processando";
    }
  };

  const blockedPatterns = useMemo(
    () =>
      new Set(
        (blocklist?.blocklist ?? []).map((b) => (b.pattern || "").toLowerCase().replace(/\.exe$/, "")),
      ),
    [blocklist],
  );

  const blockedPrograms = useMemo(
    () =>
      new Set(
        (blocklist?.blocklist ?? [])
          .map((b) => (b.programName || "").trim().toLowerCase())
          .filter(Boolean),
      ),
    [blocklist],
  );

  // Padrao enviado ao agente. O agente casa o padrao como regex contra o nome
  // do processo (sem .exe), entao escapa metacaracteres (ex.: Notepad++).
  const escapePattern = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const patternForProgram = (p: InstalledProgram) => escapePattern((p.exeName || p.name || "").trim());

  // Restaura o acompanhamento apos recarregar a pagina: se ja existe uma
  // instalacao/desinstalacao em andamento, volta a mostrar a barra.
  useEffect(() => {
    if (installReq || !data?.requests) return;
    const running = data.requests.find(
      (r) => r.type === "install-updates" && JOB_ACTIVE.includes(r.status),
    );
    if (running) setInstallReq(running);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.requests, installReq]);

  useEffect(() => {
    if (uninstallReq || !data?.requests) return;
    const running = data.requests.find(
      (r) => r.type === "uninstall-program" && JOB_ACTIVE.includes(r.status),
    );
    if (running) setUninstallReq(running);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.requests, uninstallReq]);

  // Acompanha a requisicao de instalacao de updates ate o fim.
  useEffect(() => {
    if (!installReq || !JOB_ACTIVE.includes(installReq.status)) return;
    let cancelled = false;
    let timer = 0;
    const tick = async () => {
      try {
        const r = await api<ActionRequest>(`/api/v3/requests/${installReq.uid}`);
        if (cancelled) return;
        setInstallReq(r);
        if (!JOB_ACTIVE.includes(r.status)) {
          const out = jobResult(r);
          if (r.status === "completed") {
            setFormError(null);
            setSuccess(
              out.rebootRequired
                ? "Atualizacoes instaladas. Reinicie o computador para finalizar."
                : "Atualizacoes instaladas com sucesso.",
            );
            window.setTimeout(() => reloadUpdates(), 1500);
          } else {
            setFormError(r.error || out.output || "A instalacao de atualizacoes falhou.");
          }
          return;
        }
      } catch {
        /* tenta de novo no proximo ciclo */
      }
      if (!cancelled) timer = window.setTimeout(tick, 3000);
    };
    timer = window.setTimeout(tick, 2000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installReq?.uid, installReq?.status]);

  // Acompanha a requisicao de desinstalacao ate o fim.
  useEffect(() => {
    if (!uninstallReq || !JOB_ACTIVE.includes(uninstallReq.status)) return;
    let cancelled = false;
    let timer = 0;
    const tick = async () => {
      try {
        const r = await api<ActionRequest>(`/api/v3/requests/${uninstallReq.uid}`);
        if (cancelled) return;
        setUninstallReq(r);
        if (!JOB_ACTIVE.includes(r.status)) {
          const out = jobResult(r);
          if (r.status === "completed") {
            setFormError(null);
            setSuccess(out.output || "Programa desinstalado.");
            window.setTimeout(() => {
              reloadPrograms();
              reloadBlocklist();
            }, 1500);
          } else {
            setFormError(r.error || out.output || "A desinstalacao falhou.");
          }
          return;
        }
      } catch {
        /* tenta de novo no proximo ciclo */
      }
      if (!cancelled) timer = window.setTimeout(tick, 3000);
    };
    timer = window.setTimeout(tick, 2000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uninstallReq?.uid, uninstallReq?.status]);

  async function downloadBiosDump(d: BiosDump) {
    try {
      const blob = await apiBlob(`/api/v3/bios-dumps/${d.uid}/download`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      setSuccess(err instanceof ApiError ? err.message : "Falha ao baixar dump");
    }
  }

  // Processos em execucao â€” base do dropdown de bloqueio (o agente casa o
  // padrao contra o nome do processo, ex.: "chrome"). Busca na montagem e
  // repete ate 3x enquanto vazio (o endpoint cria o comando de coleta e o
  // agente responde no proximo checkin, ~10-20s).
  const [procs, setProcs] = useState<ProcessesResponse["processes"]>([]);
  const [procsLoading, setProcsLoading] = useState(false);
  const [blockSource, setBlockSource] = useState<"proc" | "manual">("proc");

  const loadProcesses = useCallback(async () => {
    setProcsLoading(true);
    try {
      const res = await api<ProcessesResponse>(`/api/v3/devices/${uid}/processes`);
      setProcs(res.processes ?? []);
    } catch {
      setProcs([]);
    } finally {
      setProcsLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    let tries = 0;
    let timer = 0;
    const tick = async () => {
      try {
        const res = await api<ProcessesResponse>(`/api/v3/devices/${uid}/processes`);
        if (cancelled) return;
        setProcs(res.processes ?? []);
        if ((res.processes?.length ?? 0) === 0 && tries < 3) {
          tries += 1;
          timer = window.setTimeout(tick, 8000);
        }
      } catch {
        /* proxima tentativa do botao */
      }
    };
    timer = window.setTimeout(tick, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [uid]);

  const blockOptions = useMemo(() => {
    const byName = new Map<string, { value: string; count: number; mem: number }>();
    for (const p of procs) {
      if (!p?.name) continue;
      const key = p.name.toLowerCase();
      const cur = byName.get(key);
      if (cur) {
        cur.count += 1;
        cur.mem += p.memoryMb ?? 0;
      } else {
        byName.set(key, { value: p.name, count: 1, mem: p.memoryMb ?? 0 });
      }
    }
    const blocked = new Set((blocklist?.blocklist ?? []).map((b) => b.pattern));
    return Array.from(byName.values())
      .sort((a, b) => a.value.localeCompare(b.value))
      .map((o) => {
        const isBlocked = blocked.has(o.value);
        const extras = [
          `${o.count}x`,
          o.mem > 0 ? `${Math.round(o.mem)} MB` : null,
          isBlocked ? "ja bloqueado" : null,
        ]
          .filter(Boolean)
          .join(" Â· ");
        return { value: o.value, label: `${o.value} Â· ${extras}`, blocked: isBlocked };
      });
  }, [procs, blocklist]);

  // Ao mudar para "Desbloquear", carrega a blocklist atual (fonte do dropdown)
  useEffect(() => {
    if (blockAction === "unblock") {
      setShowBlocklist(true);
      reloadBlocklist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockAction]);

  // â”€â”€ Licencas de software â”€â”€
  const [licenses, setLicenses] = useState<LicensesResponse | null>(null);
  const [licLoading, setLicLoading] = useState(false);
  const [showLicenses, setShowLicenses] = useState(false);

  const loadLicenses = useCallback(async () => {
    setLicLoading(true);
    setShowLicenses(true);
    try {
      let res = await api<LicensesResponse>(`/api/v3/devices/${uid}/licenses`);
      setLicenses(res);
      // comando recem-criado: aguarda o agente responder e tenta de novo
      for (let i = 0; i < 3 && res.commandUid && (res.count ?? 0) === 0; i++) {
        await new Promise((r) => window.setTimeout(r, 8000));
        res = await api<LicensesResponse>(`/api/v3/devices/${uid}/licenses`);
        setLicenses(res);
      }
    } catch {
      /* proxima tentativa pelo botao */
    } finally {
      setLicLoading(false);
    }
  }, [uid]);

  // â”€â”€ Saude do hardware â”€â”€
  const [healthRes, setHealthRes] = useState<HealthResponse | null>(null);
  const [hltLoading, setHltLoading] = useState(false);
  const [showHealth, setShowHealth] = useState(false);

  const loadHealth = useCallback(async () => {
    setHltLoading(true);
    setShowHealth(true);
    try {
      let res = await api<HealthResponse>(`/api/v3/devices/${uid}/health`);
      setHealthRes(res);
      for (let i = 0; i < 3 && res.commandUid && !res.health; i++) {
        await new Promise((r) => window.setTimeout(r, 8000));
        res = await api<HealthResponse>(`/api/v3/devices/${uid}/health`);
        setHealthRes(res);
      }
    } catch {
      /* proxima tentativa pelo botao */
    } finally {
      setHltLoading(false);
    }
  }, [uid]);

  function openRecoveryModal() {
    setRecPhase("confirm");
    setRecToken(null);
    setRecError(null);
    setRecAmbiguous(false);
    setRecCopied(false);
  }

  function closeRecoveryModal() {
    setRecPhase("idle");
    setRecToken(null);
    setRecError(null);
    setRecAmbiguous(false);
    setRecCopied(false);
  }

  async function copyRecoveryToken() {
    if (!recToken) return;
    try {
      await navigator.clipboard.writeText(recToken.token);
      setRecCopied(true);
    } catch {
      setRecCopied(false);
      setRecError("Nao foi possivel copiar automaticamente â€” selecione o token manualmente.");
    }
  }

  async function generateRecoveryToken() {
    setRecPhase("generating");
    setRecError(null);
    setRecAmbiguous(false);
    setRecCopied(false);
    setRecToken(null);
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), RECOVERY_TOKEN_TIMEOUT_MS);
    try {
      const res = await api<RecoveryTokenResponse>(`/api/v3/devices/${uid}/recovery-token`, {
        method: "POST",
        signal: ctrl.signal,
      });
      setRecToken(res);
      setRecPhase("done");
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        setRecAmbiguous(true);
        setRecError(
          "Tempo esgotado sem confirmacao do servidor. O token PODE ter sido gerado â€” " +
            "verifique o resultado antes de tentar novamente. Nenhum novo token sera gerado automaticamente.",
        );
      } else if (err instanceof ApiError && err.status === 401) {
        setRecError("Sessao invalida ou expirada â€” faca login novamente. Nenhum token foi gerado.");
      } else if (err instanceof ApiError && err.status === 403) {
        setRecError("Seu papel nao permite gerar tokens de recuperacao. Nenhum token foi gerado.");
      } else if (err instanceof ApiError && err.status === 404) {
        setRecError("Dispositivo nao encontrado no servidor. Nenhum token foi gerado.");
      } else if (err instanceof ApiError && err.status === 0) {
        setRecAmbiguous(true);
        setRecError(
          "Falha de conexao com o servidor. O token PODE ter sido gerado â€” " +
            "verifique o resultado antes de tentar novamente.",
        );
      } else if (err instanceof ApiError) {
        setRecError(`O servidor recusou a operacao (HTTP ${err.status}). Nenhum token foi gerado.`);
      } else {
        setRecAmbiguous(true);
        setRecError("Falha inesperada â€” verifique o resultado antes de tentar novamente.");
      }
      setRecPhase("error");
    } finally {
      window.clearTimeout(timer);
    }
  }

  if (loading && !data) return <div className="page"><Spinner /></div>;
  if (error) return <div className="page"><ErrorAlert message={error.message} /></div>;
  if (!data) return null;

  const device = data.device;
  // O comando self-update so existe a partir do agente 1.4.24. Versoes
  // anteriores se atualizam sozinhas (checagem a cada 3 min) - nao oferecer
  // um botao que o agente antigo nao entende.
  const supportsSelfUpdate = versionAtLeast(device.agentVersion, "1.4.24");
  const deviceActions = ACTIONS.filter((action) => action.type !== "self-update" || supportsSelfUpdate);

  function payloadFor(type: RequestType): Record<string, unknown> {
    switch (type) {
      case "freeze":
        return { duration: Number(freezeHours), message: freezeMessage };
      case "wipe":
        return { scope: wipeScope, confirm: wipeConfirm };
      case "file-delete":
        return { path: filePath };
      case "message":
        return { body: messageBody };
      case "script":
        return { script: scriptName, timeout: 300 };
      case "block-program":
        return { pattern: blockPattern, action: blockAction };
      case "backup":
        return { backupType };
      case "reboot":
        return { delaySeconds: Number(rebootDelay) };
      default:
        return {};
    }
  }

  async function startInstallUpdates(updateIds: string[]) {
    if (!uid) return;
    setInstallBusy(true);
    setFormError(null);
    setSuccess(null);
    try {
      const r = await api<{ commandUid: string }>(`/api/v3/devices/${uid}/updates/install`, {
        method: "POST",
        body: { updateIds },
      });
      const now = new Date().toISOString();
      setInstallReq({
        uid: r.commandUid,
        type: "install-updates",
        deviceUid: uid,
        status: "pending",
        payload: { updateIds },
        result: {},
        requestedBy: "",
        error: "",
        createdAt: now,
        startedAt: null,
        finishedAt: null,
      });
      setSuccess("Instalacao solicitada â€” acompanhe o progresso abaixo.");
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Falha ao solicitar instalacao");
    } finally {
      setInstallBusy(false);
    }
  }

  async function toggleProgramBlock(p: InstalledProgram, action: "block" | "unblock") {
    const pattern = patternForProgram(p);
    if (!uid || !pattern) return;
    setProgramBusy(p.name);
    setFormError(null);
    try {
      await api(`/api/v3/devices/${uid}/blocklist`, {
        method: "POST",
        body: { pattern, programName: p.name, version: p.version, action },
      });
      setSuccess(action === "block" ? `'${pattern}' bloqueado.` : `'${pattern}' desbloqueado.`);
      window.setTimeout(() => reloadBlocklist(), 1500);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Falha ao alterar bloqueio");
    } finally {
      setProgramBusy(null);
    }
  }

  async function confirmUninstallProgram() {
    if (!uid || !uninstallTarget) return;
    setUninstallBusy(true);
    setFormError(null);
    setSuccess(null);
    try {
      const r = await api<{ commandUid: string }>(`/api/v3/devices/${uid}/programs/uninstall`, {
        method: "POST",
        body: { name: uninstallTarget.name, version: uninstallTarget.version },
      });
      const now = new Date().toISOString();
      setUninstallReq({
        uid: r.commandUid,
        type: "uninstall-program",
        deviceUid: uid,
        status: "pending",
        payload: { name: uninstallTarget.name },
        result: {},
        requestedBy: "",
        error: "",
        createdAt: now,
        startedAt: null,
        finishedAt: null,
      });
      setSuccess(`Desinstalacao de '${uninstallTarget.name}' solicitada.`);
      setUninstallTarget(null);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Falha ao solicitar desinstalacao");
    } finally {
      setUninstallBusy(false);
    }
  }

  async function releaseFreeze() {
    if (!uid) return;
    setBusy(true);
    setFormError(null);
    try {
      await api(`/api/v3/devices/${uid}/requests`, {
        method: "POST",
        body: { type: "unfreeze", payload: {} },
      });
      setSuccess("Liberacao de freeze solicitada.");
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Falha ao liberar freeze");
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!active) return;
    if (active.type === "wipe" && !wipeConfirm) {
      setFormError("Confirme a acao de wipe antes de enviar.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await api(`/api/v3/devices/${uid}/requests`, {
        method: "POST",
        body: { type: active.type, payload: payloadFor(active.type) },
      });
      setSuccess(`Requisicao ${titleCase(active.type)} criada â€” acompanhe na fila de requisicoes.`);
      setActive(null);
      reload();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Falha ao criar requisicao");
    } finally {
      setBusy(false);
    }
  }

  async function removeDevice() {
    if (!device) return;
    setRemoving(true);
    try {
      await api(`/api/v3/devices/${device.uid}`, { method: "DELETE" });
      navigate("/devices");
    } catch (e) {
      setSuccess(null);
      setConfirmRemove(false);
      window.alert(e instanceof ApiError ? e.message : "Falha ao remover o dispositivo");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="cell-sub">
            <Link to="/devices">Dispositivos</Link> / {device.uid}
          </p>
          <h1>{device.name}</h1>
          <p className="subtle">
            {device.make} {device.model} Â· {device.osVersion} Â· {device.group?.name ?? "sem grupo"}
          </p>
        </div>
        <span className="spacer" />
        <Badge value={device.status} />
        <Badge value={device.compliance} />
        <Badge value={device.encryption} />
      </div>

      {success ? <div className="alert info">{success}</div> : null}

      <div className="grid cols-2">
        <Card title="Identificacao">
          <dl className="kv">
            <dt>UID</dt>
            <dd className="mono">{device.uid}</dd>
            <dt>Serie</dt>
            <dd className="mono">{device.serial}</dd>
            <dt>Usuario</dt>
            <dd>{device.user.name}</dd>
            <dt>Departamento</dt>
            <dd>{device.user.department}</dd>
            <dt>Grupo</dt>
            <dd>{device.group?.name ?? "â€”"}</dd>
            <dt>Risco</dt>
            <dd style={{ color: device.riskScore >= 70 ? "var(--danger)" : undefined }}>
              {device.riskScore}/100
            </dd>
          </dl>
        </Card>

        <Card title="Hardware e agente">
          <dl className="kv">
            <dt>Plataforma</dt>
            <dd>{device.platform}</dd>
            <dt>Sistema</dt>
            <dd>{device.osVersion}</dd>
            <dt>Agente</dt>
            <dd className="mono">
              {device.agentVersion}
              {!supportsSelfUpdate ? (
                <span className="cell-sub"> (atualiza automaticamente em ate 3 min)</span>
              ) : null}
            </dd>
            <dt>Ultimo contato</dt>
            <dd>{formatDate(device.lastSeen)}</dd>
            <dt>Criptografia</dt>
            <dd>{titleCase(device.encryption)}</dd>
            <dt>Conformidade</dt>
            <dd>{titleCase(device.compliance)}</dd>
          </dl>
        </Card>
      </div>

      <DeviceLocationCard deviceUid={device.uid} canEdit={can("actions.issue")} />

      <InventarioHardware raw={device.raw} />

      <Card title="Acoes remotas">
        {can("actions.issue") ? (
          <div className="toolbar">
            {deviceActions.map((action) => (
              <button
                key={action.type}
                className={`btn ${action.danger ? "danger" : "primary"}`}
                onClick={() => {
                  setActive(action);
                  setFormError(null);
                }}
              >
                {action.label}
              </button>
            ))}
            <button
              id="recovery-token-btn"
              className="btn"
              type="button"
              onClick={openRecoveryModal}
              disabled={recPhase === "generating"}
            >
              Gerar token de recuperacao
            </button>
            <button
              className="btn primary"
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setFormError(null);
                try {
                  const res = await api<{ session: { uid: string } }>(`/api/v3/devices/${uid}/remote`, {
                    method: "POST",
                    body: {},
                  });
                  navigate(`/remote/${res.session.uid}`);
                } catch (err) {
                  setFormError(err instanceof ApiError ? err.message : "Falha ao iniciar o acesso remoto");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Abrindo..." : "Acesso remoto"}
            </button>
            {data?.device.status === "frozen" ? (
              <button className="btn danger" type="button" onClick={releaseFreeze} disabled={busy}>
                {busy ? "Liberando..." : "Liberar freeze"}
              </button>
            ) : null}
          </div>
        ) : (
          <div className="alert info">
            Seu papel nao possui a permissao <code>actions.issue</code> â€” leitura apenas.
          </div>
        )}
        <div className="grid cols-2" style={{ marginTop: 16 }}>
          {deviceActions.map((a) => (
            <div key={a.type}>
              <strong>{a.label}</strong> â€” <span className="subtle">{a.description}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* â”€â”€ Programas Instalados â”€â”€ */}
      <Card title="Programas instalados">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button
            className="btn primary"
            onClick={() => {
              setShowPrograms(true);
              reloadPrograms();
            }}
          >
            {showPrograms ? "Atualizar lista" : "Ver programas instalados"}
          </button>
          {can("actions.issue") && (
            <button
              className="btn"
              onClick={async () => {
                setBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "inventory-programs" },
                  });
                  setSuccess("Coleta de programas iniciada â€” recarregue em instantes.");
                } catch {
                  setSuccess(null);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
            >
              Coletar agora
            </button>
          )}
          {programs && programs.count > 0 && (
            <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
              {programs.stored ? "Armazenado no painel" : "Resultado do agente"}
              {programs.collectedAt ? ` Â· ${formatDate(programs.collectedAt)}` : ""}
            </span>
          )}
        </div>
        {showPrograms && (
          progLoading ? <Spinner /> :
          programs && programs.count > 0 ? (
            <>
              {uninstallReq ? (
                <ProgressBar
                  percent={jobResult(uninstallReq).progress ?? 0}
                  indeterminate={isJobActive(uninstallReq) && jobResult(uninstallReq).indeterminate !== false}
                  label={
                    isJobActive(uninstallReq)
                      ? jobPhaseLabel(jobResult(uninstallReq).phase)
                      : uninstallReq.status === "completed"
                        ? "Desinstalacao concluida"
                        : "Desinstalacao"
                  }
                  detail={
                    uninstallReq.status === "completed"
                      ? jobResult(uninstallReq).output
                      : uninstallReq.status === "failed"
                        ? uninstallReq.error || jobResult(uninstallReq).output
                        : jobResult(uninstallReq).output || "Aguarde â€” o progresso aparece aqui."
                  }
                />
              ) : null}
              <div className="table-wrap" style={{ maxHeight: 400, overflow: "auto" }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Versao</th>
                      <th>Fornecedor</th>
                      <th>Instalado</th>
                      <th>Tamanho</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {programs.programs.map((p, i) => {
                      const pattern = patternForProgram(p);
                      const blocked =
                        (pattern && blockedPatterns.has(pattern.toLowerCase().replace(/\.exe$/, ""))) ||
                        blockedPrograms.has((p.name || "").trim().toLowerCase());
                      return (
                        <tr key={i}>
                          <td><div className="cell-title">{p.name}</div></td>
                          <td className="mono">{p.version || "â€”"}</td>
                          <td>{p.publisher || "â€”"}</td>
                          <td className="cell-sub">{p.installed || "â€”"}</td>
                          <td>{p.sizeMb ? `${p.sizeMb} MB` : "â€”"}</td>
                          <td>
                            {can("actions.issue") ? (
                              <div className="toolbar" style={{ gap: 6, flexWrap: "nowrap" }}>
                                <button
                                  className={`btn sm${blocked ? "" : " primary"}`}
                                  type="button"
                                  title={pattern ? `Processo: ${pattern}` : "Programa sem executavel identificado"}
                                  disabled={!pattern || programBusy === p.name}
                                  onClick={() => toggleProgramBlock(p, blocked ? "unblock" : "block")}
                                >
                                  {blocked ? "Desbloquear" : "Bloquear"}
                                </button>
                                <button
                                  className="btn sm danger"
                                  type="button"
                                  disabled={isJobActive(uninstallReq) || uninstallBusy}
                                  onClick={() => {
                                    setFormError(null);
                                    setUninstallTarget(p);
                                  }}
                                >
                                  Desinstalar
                                </button>
                              </div>
                            ) : (
                              <span className="muted">â€”</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="subtle" style={{ marginTop: 8 }}>
                Bloquear usa o executavel identificado no inventario e fecha o processo em ate ~30 s se for reaberto.
                Desinstalar executa o desinstalador silencioso do proprio programa â€” alguns instaladores ainda podem
                pedir interacao no computador.
              </p>
            </>
          ) : programs && programs.stored === false ? (
            <Empty>Coletando a lista de programas no computador... isso pode levar alguns segundos.</Empty>
          ) : <Empty>Nenhum programa armazenado no painel ainda â€” a coleta automatica do agente envia a lista no proximo checkin.</Empty>
        )}
      </Card>

      {/* â”€â”€ Windows Updates â”€â”€ */}
      <Card title="Windows Updates">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button
            className="btn primary"
            onClick={() => {
              setShowUpdates(true);
              reloadUpdates();
            }}
          >
            {showUpdates ? "Atualizar" : "Ver atualizacoes pendentes"}
          </button>
          {can("actions.issue") && (
            <button
              className="btn"
              onClick={async () => {
                setBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "inventory-updates" },
                  });
                  setSuccess("Verificacao de atualizacoes iniciada.");
                } catch { setSuccess(null); } finally { setBusy(false); }
              }}
              disabled={busy}
            >
              Verificar agora
            </button>
          )}
          {can("actions.issue") && updates && updates.count > 0 ? (
            <button
              className="btn primary"
              type="button"
              onClick={() => startInstallUpdates([])}
              disabled={installBusy || isJobActive(installReq)}
            >
              {installBusy ? "Solicitando..." : "Instalar todas"}
            </button>
          ) : null}
        </div>
        {installReq ? (
          <ProgressBar
            percent={jobResult(installReq).progress ?? 0}
            indeterminate={isJobActive(installReq) && jobResult(installReq).indeterminate !== false}
            label={
              isJobActive(installReq)
                ? jobPhaseLabel(jobResult(installReq).phase)
                : installReq.status === "completed"
                  ? "Atualizacoes instaladas"
                  : "Instalacao de atualizacoes"
            }
            detail={
              installReq.status === "completed"
                ? jobResult(installReq).output ||
                  (jobResult(installReq).rebootRequired
                    ? "Reinicie o computador para finalizar."
                    : "Concluido.")
                : installReq.status === "failed"
                  ? installReq.error || jobResult(installReq).output || "Falhou."
                  : [
                      jobResult(installReq).current && jobResult(installReq).total
                        ? `${jobResult(installReq).current} de ${jobResult(installReq).total}`
                        : null,
                      jobResult(installReq).output,
                      "a maquina continua enviando check-in durante a instalacao.",
                    ]
                      .filter(Boolean)
                      .join(" Â· ")
            }
          />
        ) : null}
        {showUpdates && (
          updLoading ? <Spinner /> :
          updates ? (
            <>
              <p className="subtle">Ultima verificacao: {formatDate(updates.lastCheck)}</p>
              {updates.count === 0 ? (
                updates.fresh === false ? (
                  <Empty>Consultando atualizacoes no computador... isso pode levar alguns segundos.</Empty>
                ) : (
                  <Empty>Nenhuma atualizacao pendente.</Empty>
                )
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Atualizacao</th>
                        <th>Severidade</th>
                        <th>Tamanho</th>
                        <th>Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {updates.updates.map((u: WindowsUpdate, i: number) => (
                        <tr key={i}>
                          <td>
                            <div className="cell-title">{u.title}</div>
                            {u.kb || u.kbId ? (
                              <div className="cell-sub mono">{u.kb || u.kbId}</div>
                            ) : null}
                          </td>
                          <td><Badge value={u.severity} /></td>
                          <td>{u.sizeMb ? `${u.sizeMb} MB` : "â€”"}</td>
                          <td>
                            {can("actions.issue") ? (
                              <button
                                className="btn sm primary"
                                type="button"
                                disabled={!u.updateId || installBusy || isJobActive(installReq)}
                                title={
                                  u.updateId
                                    ? "Instalar somente esta atualizacao"
                                    : "Atualize a lista (Verificar agora) para liberar a instalacao individual"
                                }
                                onClick={() => u.updateId && startInstallUpdates([u.updateId])}
                              >
                                Instalar
                              </button>
                            ) : (
                              <span className="muted">â€”</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : <Empty>Nenhuma verificacao realizada.</Empty>
        )}
      </Card>

      {/* â”€â”€ Licencas de software â”€â”€ */}
      <Card title="Licencas de software">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={loadLicenses} disabled={licLoading}>
            {licLoading ? "Coletando..." : showLicenses ? "Atualizar" : "Ver licencas"}
          </button>
          {licenses?.collectedAt ? (
            <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>
              {licenses.stored ? "Seriais salvos no painel" : "Coletado em"} {formatDate(licenses.collectedAt)}
            </span>
          ) : null}
        </div>
        {showLicenses ? (
          licLoading && !licenses ? (
            <Spinner />
          ) : licenses && licenses.licenses.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Tipo</th>
                    <th>Canal</th>
                    <th>Chave</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.licenses.map((l, i) => {
                    const b = licenseBadge(l.status);
                    return (
                      <tr key={i}>
                        <td><div className="cell-title">{l.product}</div></td>
                        <td>{titleCase(l.kind)}</td>
                        <td className="cell-sub">{l.channel || "â€”"}</td>
                        <td className="mono cell-sub">{l.productKey || l.partialKey || "â€”"}</td>
                        <td><Badge value={b.value} label={b.label} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty>Nenhum serial armazenado no painel ainda â€” o agente envia os seriais na varredura automatica (proximos checkins).</Empty>
          )
        ) : null}
      </Card>

      {/* â”€â”€ Saude do hardware â”€â”€ */}
      <Card title="Saude do hardware">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={loadHealth} disabled={hltLoading}>
            {hltLoading ? "Coletando..." : showHealth ? "Atualizar" : "Ver saude do hardware"}
          </button>
          {healthRes?.collectedAt ? (
            <span className="muted" style={{ marginLeft: "auto" }}>
              Coletado em {formatDate(healthRes.collectedAt)}
            </span>
          ) : null}
        </div>
        {showHealth ? (
          hltLoading && !healthRes ? (
            <Spinner />
          ) : healthRes?.health ? (
            <>
              {healthRes.health.issues.length > 0 ? (
                <div style={{ marginBottom: 12 }}>
                  {healthRes.health.issues.map((issue, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                      <Badge value="warning" label="Atencao" />
                      <span>{issue}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="subtle" style={{ marginBottom: 12 }}>Nenhum problema detectado.</p>
              )}

              {healthRes.health.disks.length > 0 ? (
                <div className="table-wrap" style={{ marginBottom: 12 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Disco</th>
                        <th>Midia</th>
                        <th>Tamanho</th>
                        <th>Saude</th>
                        <th>Desgaste</th>
                        <th>Temp</th>
                        <th>Erros R/W</th>
                        <th>Horas ligado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {healthRes.health.disks.map((d, i) => (
                        <tr key={i}>
                          <td>
                            <div className="cell-title">{d.model || "Disco"}</div>
                            <div className="cell-sub mono">{d.serial || "â€”"}</div>
                          </td>
                          <td className="cell-sub">{[d.media, d.bus].filter(Boolean).join(" Â· ") || "â€”"}</td>
                          <td className="cell-sub">{d.sizeGb} GB</td>
                          <td>
                            <Badge
                              value={d.health === "healthy" ? "completed" : "critical"}
                              label={d.health === "healthy" ? "Saudavel" : d.health}
                            />
                          </td>
                          <td className="cell-sub">
                            {d.wear != null ? `${d.wear}%` : "â€”"}
                          </td>
                          <td className="cell-sub">{d.temperature != null ? `${d.temperature} C` : "â€”"}</td>
                          <td className="cell-sub">{d.readErrors ?? 0} / {d.writeErrors ?? 0}</td>
                          <td className="cell-sub">{d.powerOnHours != null ? `${d.powerOnHours} h` : "â€”"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              <div className="grid cols-2" style={{ alignItems: "start" }}>
                <section className="panel">
                  <div className="panel-title">Memoria</div>
                  <dl className="kv">
                    <dt>Total</dt>
                    <dd>{healthRes.health.memory ? `${healthRes.health.memory.totalGb} GB` : "â€”"}</dd>
                    <dt>Slots</dt>
                    <dd>
                      {healthRes.health.memory
                        ? healthRes.health.memory.slotsTotal
                          ? `${healthRes.health.memory.modules}/${healthRes.health.memory.slotsTotal} preenchidos`
                          : `${healthRes.health.memory.modules} modulo(s)`
                        : "â€”"}
                    </dd>
                    <dt>Velocidade</dt>
                    <dd>{healthRes.health.memory?.speedMax ? `${healthRes.health.memory.speedMax} MHz` : "â€”"}</dd>
                    <dt>Erros WHEA (30d)</dt>
                    <dd>
                      {healthRes.health.memory ? (
                        <Badge
                          value={healthRes.health.memory.wheaEvents30d > 0 ? "warning" : "completed"}
                          label={healthRes.health.memory.wheaEvents30d > 0 ? `${healthRes.health.memory.wheaEvents30d} evento(s)` : "Nenhum"}
                        />
                      ) : "â€”"}
                    </dd>
                    {healthRes.health.memory?.lastDiag ? (
                      <>
                        <dt>Diagnostico</dt>
                        <dd className="cell-sub">{healthRes.health.memory.lastDiag}</dd>
                      </>
                    ) : null}
                  </dl>
                </section>

                <section className="panel">
                  <div className="panel-title">Sistema</div>
                  <dl className="kv">
                    <dt>Placa-mae</dt>
                    <dd>{healthRes.health.system?.motherboard || "â€”"}</dd>
                    <dt>BIOS</dt>
                    <dd>
                      {healthRes.health.system?.bios
                        ? `${healthRes.health.system.bios}${healthRes.health.system.biosDate ? ` (${healthRes.health.system.biosDate})` : ""}`
                        : "â€”"}
                    </dd>
                    <dt>Tempo ligado</dt>
                    <dd>
                      {healthRes.health.system?.uptimeHours != null
                        ? healthRes.health.system.uptimeHours >= 24
                          ? `${Math.floor(healthRes.health.system.uptimeHours / 24)} d ${healthRes.health.system.uptimeHours % 24} h`
                          : `${healthRes.health.system.uptimeHours} h`
                        : "â€”"}
                    </dd>
                  </dl>
                </section>

                <section className="panel">
                  <div className="panel-title">Bateria</div>
                  <dl className="kv">
                    <dt>Carga</dt>
                    <dd>
                      {healthRes.health.battery
                        ? healthRes.health.battery.charge != null
                          ? `${healthRes.health.battery.charge}%`
                          : "â€”"
                        : "Sem bateria (desktop)"}
                    </dd>
                    {healthRes.health.battery?.healthPct != null ? (
                      <>
                        <dt>Saude</dt>
                        <dd>
                          <Badge
                            value={healthRes.health.battery.healthPct < 60 ? "warning" : "completed"}
                            label={`${healthRes.health.battery.healthPct}%`}
                          />
                        </dd>
                      </>
                    ) : null}
                  </dl>
                </section>

                <section className="panel">
                  <div className="panel-title">CPU</div>
                  <dl className="kv">
                    <dt>Processador</dt>
                    <dd>{healthRes.health.cpu?.name || "â€”"}</dd>
                    <dt>Clock maximo</dt>
                    <dd>{healthRes.health.cpu?.maxClockGhz != null ? `${healthRes.health.cpu.maxClockGhz} GHz` : "â€”"}</dd>
                    <dt>Temperatura</dt>
                    <dd>{healthRes.health.cpu?.tempC != null ? `${healthRes.health.cpu.tempC} C` : "â€”"}</dd>
                    <dt>Carga</dt>
                    <dd>{healthRes.health.cpu?.loadPct != null ? `${healthRes.health.cpu.loadPct}%` : "â€”"}</dd>
                    <dt>Nucleos / threads</dt>
                    <dd>
                      {healthRes.health.cpu?.cores != null
                        ? `${healthRes.health.cpu.cores}C/${healthRes.health.cpu.threads ?? "?"}T`
                        : "â€”"}
                    </dd>
                  </dl>
                </section>
              </div>
            </>
          ) : (
            <Empty>Sem dados de saude â€” o agente responde no proximo checkin (~10-20s). Clique em "Atualizar".</Empty>
          )
        ) : null}
      </Card>

      {/* â”€â”€ Bloqueio de Programas â”€â”€ */}
      <Card title="Bloqueio de programas">
        {can("actions.issue") ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!blockPattern) return;
              setBusy(true);
              setFormError(null);
              try {
                await api(`/api/v3/devices/${uid}/blocklist`, {
                  method: "POST",
                  body: { pattern: blockPattern, action: blockAction },
                });
                setSuccess(`Programa '${blockPattern}' ${blockAction === "block" ? "bloqueado" : "desbloqueado"}.`);
                setBlockPattern("");
                reloadBlocklist();
              } catch (err) {
                setFormError(err instanceof ApiError ? err.message : "Falha");
              } finally {
                setBusy(false);
              }
            }}
          >
            <div className="grid cols-3" style={{ gap: 12 }}>
              <div className="field">
                <label htmlFor="block-program-select">Programa</label>
                <select
                  id="block-program-select"
                  className="select"
                  value={blockSource === "manual" ? "__manual__" : blockPattern}
                  onChange={(e) => {
                    if (e.target.value === "__manual__") {
                      setBlockSource("manual");
                      setBlockPattern("");
                    } else {
                      setBlockSource("proc");
                      setBlockPattern(e.target.value);
                    }
                  }}
                >
                  <option value="" disabled>
                    {blockAction === "unblock"
                      ? (blocklist?.blocklist?.length ?? 0) > 0
                        ? "Selecione o bloqueio a remover..."
                        : "Nenhum bloqueio ativo"
                      : blockOptions.length > 0
                        ? "Selecione um programa..."
                        : procsLoading
                          ? "Coletando processos..."
                          : "Processos ainda nao coletados"}
                  </option>
                  {blockAction === "unblock" ? (
                    (blocklist?.blocklist ?? []).length > 0 ? (
                      <optgroup label={`Bloqueios ativos (${blocklist?.blocklist?.length ?? 0})`}>
                        {(blocklist?.blocklist ?? []).map((b) => (
                          <option key={b.pattern} value={b.pattern}>
                            {b.pattern} Â· {formatDate(b.added)}
                          </option>
                        ))}
                      </optgroup>
                    ) : null
                  ) : blockOptions.length > 0 ? (
                    <optgroup label={`Processos em execucao (${blockOptions.length})`}>
                      {blockOptions.map((o) => (
                        <option key={o.value} value={o.value} disabled={o.blocked}>
                          {o.label}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  <option value="__manual__">Outro (digitar padrao)</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="block-action">Acao</label>
                <select
                  id="block-action"
                  className="select"
                  value={blockAction}
                  onChange={(e) => {
                    setBlockAction(e.target.value as "block" | "unblock");
                    setBlockPattern("");
                    setBlockSource("proc");
                  }}
                >
                  <option value="block">Bloquear</option>
                  <option value="unblock">Desbloquear</option>
                </select>
              </div>
              <div className="field" style={{ display: "flex", alignItems: "flex-end" }}>
                <button className="btn primary" type="submit" disabled={busy || !blockPattern}>
                  {busy ? "Enviando..." : "Aplicar"}
                </button>
              </div>
            </div>
            {blockSource === "manual" ? (
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor="block-pattern">Padrao do processo (regex, sem .exe)</label>
                <input
                  id="block-pattern"
                  className="input"
                  placeholder="ex: chrome"
                  value={blockPattern}
                  onChange={(e) => setBlockPattern(e.target.value)}
                />
              </div>
            ) : null}
            {blockAction === "block" && blockOptions.length === 0 && !procsLoading ? (
              <p className="subtle" style={{ marginTop: 8 }}>
                Nenhum processo coletado ainda â€” o agente responde no proximo checkin (~10-20s).
                Clique em "Atualizar processos" apos esse periodo, ou use "Outro (digitar padrao)".
              </p>
            ) : null}
            {formError ? <ErrorAlert message={formError} /> : null}
          </form>
        ) : (
          <div className="alert info">Seu papel nao permite alterar o bloqueio.</div>
        )}
        <div className="toolbar" style={{ marginTop: 12 }}>
          {can("actions.issue") ? (
            <button className="btn" type="button" onClick={loadProcesses} disabled={procsLoading}>
              {procsLoading ? "Coletando..." : "Atualizar processos"}
            </button>
          ) : null}
          <button
            className="btn"
            onClick={() => {
              setShowBlocklist(true);
              reloadBlocklist();
            }}
          >
            {showBlocklist ? "Atualizar lista" : "Ver bloqueios atuais"}
          </button>
        </div>
        {showBlocklist && (
          blLoading ? <Spinner /> :
          blocklist && blocklist.blocklist.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Padrao</th><th>Adicionado</th><th>Por</th></tr>
                </thead>
                <tbody>
                  {blocklist.blocklist.map((b, i) => (
                    <tr key={i}>
                      <td className="mono">{b.pattern}</td>
                      <td className="cell-sub">{formatDate(b.added)}</td>
                      <td>{b.addedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : blocklist?.refreshing ? (
            <Empty>Atualizando a lista de bloqueios com o computador...</Empty>
          ) : <Empty>Nenhum programa bloqueado.</Empty>
        )}
      </Card>

      {/* â”€â”€ Backups de Seguranca â”€â”€ */}
      <Card title="Backups de seguranca">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          {can("actions.issue") && (
            <>
              <select className="select" value={backupType} onChange={(e) => setBackupType(e.target.value)} style={{ maxWidth: 160 }}>
                <option value="quick">Backup rapido</option>
                <option value="full">Backup completo</option>
              </select>
              <button
                className="btn primary"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api(`/api/v3/devices/${uid}/backup`, {
                      method: "POST",
                      body: { backupType },
                    });
                    setSuccess("Backup iniciado â€” acompanhe na lista.");
                    setTimeout(() => reloadBackups(), 2000);
                  } catch { setSuccess(null); } finally { setBusy(false); }
                }}
                disabled={busy}
              >
                {busy ? "Iniciando..." : "Executar backup"}
              </button>
            </>
          )}
          <button
            className="btn"
            onClick={() => {
              setShowBackups(true);
              reloadBackups();
            }}
          >
            {showBackups ? "Atualizar" : "Ver backups"}
          </button>
        </div>
        {showBackups && (
          bkLoading ? <Spinner /> :
          backups && backups.backups.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>UID</th><th>Tipo</th><th>Data</th><th>Itens</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {backups.backups.map((b) => (
                    <tr key={b.uid}>
                      <td className="mono cell-sub">{b.uid}</td>
                      <td>{titleCase(b.type)}</td>
                      <td className="cell-sub">{formatDate(b.timestamp)}</td>
                      <td>{b.results.length} item(ns)</td>
                      <td>
                        {b.results.some((r) => r.status === "error") ? (
                          <Badge value="error" />
                        ) : (
                          <Badge value="ok" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty>Nenhum backup registrado.</Empty>
        )}
      </Card>

      {/* â”€â”€ Backup de BIOS (ROM via driver, somente leitura) â”€â”€ */}
      <Card title="Backup de BIOS (ROM)">
        <div className="toolbar" style={{ marginBottom: 12 }}>
          {can("actions.issue") && (
            <button
              className="btn primary"
              type="button"
              disabled={biosBusy || Boolean(biosDumps?.pending)}
              onClick={async () => {
                setBiosBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "bios_backup", payload: { allowTestMode } },
                  });
                  setSuccess(
                    allowTestMode
                      ? "Backup de BIOS solicitado â€” o agente ativa o modo teste (consentido) e conclui apos o reinicio."
                      : "Backup de BIOS solicitado â€” sem alterar o Windows. Se o driver nao for aceito, o agente reporta a necessidade de modo teste.",
                  );
                  setShowBios(true);
                  setTimeout(() => reloadBios(), 2500);
                } catch {
                  setSuccess(null);
                } finally {
                  setBiosBusy(false);
                }
              }}
            >
              {biosBusy ? "Solicitando..." : "Solicitar dump de BIOS"}
            </button>
          )}
          <button
            className="btn"
            onClick={() => {
              setShowBios(true);
              reloadBios();
            }}
          >
            {showBios ? "Atualizar" : "Ver dumps"}
          </button>
          {can("actions.issue") && (
            <button
              className="btn"
              type="button"
              disabled={biosBusy}
              onClick={async () => {
                if (!window.confirm("Reverter modo de teste? Isto remove o driver BiosRead e desativa o testsigning (o watermark Test Mode some apos reiniciar).")) return;
                setBiosBusy(true);
                try {
                  await api(`/api/v3/devices/${uid}/requests`, {
                    method: "POST",
                    body: { type: "bios_revert", payload: {} },
                  });
                  setSuccess("Reversao solicitada â€” apos o agente executar, reinicie a estacao para remover o watermark.");
                } catch {
                  setSuccess(null);
                } finally {
                  setBiosBusy(false);
                }
              }}
            >
              Reverter modo teste
            </button>
          )}
        </div>
        <p className="subtle" style={{ marginTop: -4, marginBottom: 12 }}>
          Os dumps sao gravados somente no servidor do painel; apos o upload o agente apaga a copia local do computador.
        </p>
        {can("actions.issue") && (
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 12, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={allowTestMode}
              onChange={(e) => setAllowTestMode(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: 13, color: "var(--muted, #9aa4b2)" }}>
              <strong style={{ color: "var(--text, #e6e9ef)" }}>Permitir modo de teste do Windows</strong> nesta estacao
              (testsigning) â€” <u>necessario</u> para carregar o driver nao assinado; mostra watermark &quot;Test Mode&quot;
              e exige reinicio. Use apenas em maquinas de teste.
            </span>
          </label>
        )}
        {biosDumps?.pending && (
          <div className="panel" style={{ marginBottom: 12 }}>
            <div className="panel-title">Dump em andamento</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span className="mono">{biosDumps.pending.uid}</span>
              <Badge value={biosDumps.pending.status} />
            </div>
            <div style={{ marginTop: 6, fontSize: 13 }}>
              {biosDumps.pending.output ||
                biosDumps.pending.error ||
                (biosDumps.pending.status === "running"
                  ? "o agente esta executando o dump em segundo plano"
                  : "aguardando o proximo checkin do agente")}
            </div>
            {can("actions.issue") ? (
              <button
                className="btn sm"
                type="button"
                style={{ marginTop: 8 }}
                disabled={biosBusy}
                onClick={async () => {
                  const p = biosDumps?.pending;
                  if (!p) return;
                  if (!window.confirm("Cancelar este pedido de BIOS? O agente descarta o estado local e encerra o worker no proximo checkin.")) return;
                  setBiosBusy(true);
                  try {
                    await api(`/api/v3/requests/${p.uid}/cancel`, { method: "POST" });
                    setSuccess("Pedido de BIOS cancelado â€” o agente descarta o estado no proximo checkin.");
                    setTimeout(() => reloadBios(), 1500);
                  } catch (err) {
                    setSuccess(err instanceof ApiError ? err.message : "Falha ao cancelar o pedido");
                  } finally {
                    setBiosBusy(false);
                  }
                }}
              >
                Cancelar pedido
              </button>
            ) : null}
          </div>
        )}
        {biosDumps?.lastRequest && !biosDumps.pending && (
          <div className="panel" style={{ marginBottom: 12 }}>
            <div className="panel-title">Ultimo pedido de backup</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <Badge
                value={
                  biosDumps.lastRequest.status === "failed" ||
                  (biosDumps.lastRequest.exitCode ?? 0) !== 0 ||
                  biosDumps.lastRequest.error
                    ? "error"
                    : "ok"
                }
                label={
                  biosDumps.lastRequest.status === "failed" ||
                  (biosDumps.lastRequest.exitCode ?? 0) !== 0 ||
                  biosDumps.lastRequest.error
                    ? "falhou"
                    : "ok"
                }
              />
              <span className="mono cell-sub">{biosDumps.lastRequest.uid}</span>
              <span className="cell-sub">{formatDate(biosDumps.lastRequest.finishedAt ?? biosDumps.lastRequest.createdAt)}</span>
            </div>
            <div style={{ marginTop: 6, fontSize: 13 }}>
              {biosDumps.lastRequest.output || biosDumps.lastRequest.error || "sem mensagem do agente"}
            </div>
          </div>
        )}
        {showBios && (
          biosLoading ? <Spinner /> :
          biosDumps && biosDumps.dumps.length > 0 ? (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Arquivo</th><th>Tamanho</th><th>FVs</th><th>Gaps</th><th>SHA256</th><th>Enviado</th><th></th></tr>
                </thead>
                <tbody>
                  {biosDumps.dumps.map((d) => (
                    <tr key={d.uid}>
                      <td className="mono">{d.filename}</td>
                      <td className="cell-sub">{(d.bytes / 1048576).toFixed(1)} MB</td>
                      <td>{d.fvh}</td>
                      <td>{d.gaps > 0 ? <Badge value="warning" label={`${d.gaps}`} /> : <Badge value="ok" />}</td>
                      <td className="mono cell-sub">{d.sha256.slice(0, 12)}â€¦</td>
                      <td className="cell-sub">{formatDate(d.uploadedAt)}</td>
                      <td>
                        <button className="btn" type="button" onClick={() => downloadBiosDump(d)}>
                          Baixar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <Empty>Nenhum dump de BIOS. Os backups ficam armazenados no servidor do painel (nada e mantido no computador).</Empty>
        )}
        <BiosReferences make={device.make || ""} model={device.model || ""} />
      </Card>

      <div className="grid cols-2">
        <Card title="Eventos" tight>
          {data.events.length === 0 ? (
            <Empty>Nenhum evento registrado.</Empty>
          ) : (
            <div style={{ padding: "0 16px" }}>
              <div className="timeline">
                {data.events.map((e) => (
                  <div className="timeline-item" key={e.id}>
                    <span className="when">{formatDate(e.at)}</span>
                    <span>
                      <div className="cell-sub mono">{e.type}</div>
                      <div>{maskRecoveryToken(e.message)}</div>
                      <Badge value={e.severity} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card title="Requisicoes deste dispositivo" tight>
          {data.requests.length === 0 ? (
            <Empty>Nenhuma requisicao enviada.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Solicitante</th>
                    <th>Criada</th>
                  </tr>
                </thead>
                <tbody>
                  {data.requests.map((r) => (
                    <tr key={r.uid}>
                      <td>
                        <div className="cell-title">{titleCase(r.type)}</div>
                        <div className="cell-sub mono">{r.uid}</div>
                      </td>
                      <td>
                        <Badge value={r.status} />
                        {r.error ? <div className="cell-sub">{r.error}</div> : null}
                      </td>
                      <td className="cell-sub">{r.requestedBy}</td>
                      <td className="cell-sub">{relativeTime(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {active ? (
        <Modal
          title={`${active.label} â€” ${device.name}`}
          onClose={() => setActive(null)}
          footer={
            <>
              <button className="btn" onClick={() => setActive(null)} disabled={busy}>
                Cancelar
              </button>
              <button
                className={`btn ${active.danger ? "danger" : "primary"}`}
                onClick={submit}
                disabled={busy}
              >
                {busy ? "Enviando..." : "Confirmar acao"}
              </button>
            </>
          }
        >
          <p className="subtle">{active.description}</p>
          {formError ? <ErrorAlert message={formError} /> : null}

          {active.type === "reboot" ? (
            <div className="field">
              <label htmlFor="reboot-delay">Atraso antes de reiniciar (segundos)</label>
              <input
                id="reboot-delay"
                type="number"
                min={15}
                max={900}
                value={rebootDelay}
                onChange={(e) => setRebootDelay(Number(e.target.value))}
              />
              <p className="subtle" style={{ marginTop: 8 }}>
                O relatorio do comando e enviado ao painel antes do reinicio. Avise o usuario para salvar
                o trabalho; o computador sera reiniciado a forca apos o atraso.
              </p>
            </div>
          ) : null}

          {active.type === "freeze" ? (
            <>
              <div className="field">
                <label htmlFor="freeze-hours">Duracao (horas)</label>
                <select id="freeze-hours" className="select" value={freezeHours} onChange={(e) => setFreezeHours(e.target.value)}>
                  <option value="1">1 hora</option>
                  <option value="4">4 horas</option>
                  <option value="24">24 horas</option>
                  <option value="72">72 horas</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="freeze-msg">Mensagem exibida</label>
                <input id="freeze-msg" className="input" value={freezeMessage} onChange={(e) => setFreezeMessage(e.target.value)} />
              </div>
            </>
          ) : null}

          {active.type === "wipe" ? (
            <>
              <div className="field">
                <label htmlFor="wipe-scope">Escopo</label>
                <select id="wipe-scope" className="select" value={wipeScope} onChange={(e) => setWipeScope(e.target.value)}>
                  <option value="data">Dados e contas (mantem o Windows)</option>
                  <option value="full-disk">Dados + sobrescrever espaco livre</option>
                </select>
              </div>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.checked)} />
                Confirmo que esta acao e irreversivel
              </label>
            </>
          ) : null}

          {active.type === "file-delete" ? (
            <div className="field">
              <label htmlFor="file-path">Caminho do arquivo</label>
              <input id="file-path" className="input mono" value={filePath} onChange={(e) => setFilePath(e.target.value)} />
            </div>
          ) : null}

          {active.type === "message" ? (
            <div className="field">
              <label htmlFor="msg-body">Corpo da mensagem</label>
              <textarea id="msg-body" className="input" rows={3} value={messageBody} onChange={(e) => setMessageBody(e.target.value)} />
            </div>
          ) : null}

          {active.type === "script" ? (
            <div className="field">
              <label htmlFor="script-name">Script da biblioteca</label>
              <select id="script-name" className="select" value={scriptName} onChange={(e) => setScriptName(e.target.value)}>
                <option value="coletar-inventario">coletar-inventario</option>
                <option value="renovar-certificado">renovar-certificado</option>
                <option value="verificar-agent">verificar-agent</option>
              </select>
            </div>
          ) : null}

          {active.type === "block-program" ? (
            <>
              <div className="field">
                <label htmlFor="blk-pattern">Nome do programa</label>
                <input id="blk-pattern" className="input mono" placeholder="ex: notepad.exe" value={blockPattern} onChange={(e) => setBlockPattern(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="blk-action">Acao</label>
                <select id="blk-action" className="select" value={blockAction} onChange={(e) => setBlockAction(e.target.value as "block" | "unblock")}>
                  <option value="block">Bloquear</option>
                  <option value="unblock">Desbloquear</option>
                </select>
              </div>
            </>
          ) : null}

          {active.type === "backup" ? (
            <div className="field">
              <label htmlFor="bk-type">Tipo de backup</label>
              <select id="bk-type" className="select" value={backupType} onChange={(e) => setBackupType(e.target.value)}>
                <option value="quick">Rapido (docs + registro)</option>
                <option value="full">Completo (perfil de usuario)</option>
              </select>
            </div>
          ) : null}

          {active.type === "inventory-programs" || active.type === "inventory-updates" ||
           active.type === "install-updates" || active.type === "inventory-processes" ||
           active.type === "get-blocklist" ? (
            <div className="alert info">
              Esta acao coleta dados no endpoint. O resultado ficara disponivel nas secoes abaixo apos o agente reportar.
            </div>
          ) : null}
        </Modal>
      ) : null}

      {uninstallTarget ? (
        <Modal
          title="Desinstalar programa"
          onClose={() => {
            if (!uninstallBusy) setUninstallTarget(null);
          }}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setUninstallTarget(null)} disabled={uninstallBusy}>
                Cancelar
              </button>
              <button className="btn danger" type="button" onClick={confirmUninstallProgram} disabled={uninstallBusy}>
                {uninstallBusy ? "Enviando..." : "Desinstalar agora"}
              </button>
            </>
          }
        >
          <p>
            Desinstalar <strong>{uninstallTarget.name}</strong>
            {uninstallTarget.version ? ` (${uninstallTarget.version})` : ""} em <strong>{device.name}</strong>?
          </p>
          <p className="subtle">
            O agente executa o desinstalador silencioso registrado pelo proprio programa. Esta acao nao pode ser
            desfeita.
          </p>
          {formError ? <ErrorAlert message={formError} /> : null}
        </Modal>
      ) : null}

      {recPhase !== "idle" ? (
        <Modal
          title={`Token de recuperacao â€” ${device.name}`}
          onClose={closeRecoveryModal}
          footer={
            recPhase === "confirm" ? (
              <>
                <button className="btn" type="button" onClick={closeRecoveryModal}>
                  Cancelar
                </button>
                <button
                  id="recovery-confirm-btn"
                  className="btn primary"
                  type="button"
                  onClick={generateRecoveryToken}
                >
                  Confirmar e gerar token
                </button>
              </>
            ) : recPhase === "generating" ? (
              <>
                <button className="btn" type="button" disabled>
                  Cancelar
                </button>
                <button id="recovery-confirm-btn" className="btn primary" type="button" disabled>
                  Gerando token...
                </button>
              </>
            ) : recPhase === "done" ? (
              <>
                <button id="recovery-copy-btn" className="btn" type="button" onClick={copyRecoveryToken}>
                  {recCopied ? "Copiado!" : "Copiar token"}
                </button>
                <button id="recovery-close-btn" className="btn primary" type="button" onClick={closeRecoveryModal}>
                  Fechar
                </button>
              </>
            ) : (
              <button id="recovery-close-btn" className="btn" type="button" onClick={closeRecoveryModal}>
                Fechar
              </button>
            )
          }
        >
          {recPhase === "confirm" ? (
            <>
              <p>
                Confirma a geracao de um token de recuperacao para{" "}
                <strong>{device.name}</strong> (<span className="mono">{device.uid}</span>)?
              </p>
              <div className="alert info">
                O token e de uso unico, vale 24 horas e sera exibido somente uma vez.
                Ele nao fica salvo no console apos fechar esta janela.
              </div>
            </>
          ) : null}

          {recPhase === "generating" ? (
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <Spinner />
              <span>Gerando token no servidor â€” aguarde...</span>
            </div>
          ) : null}

          {recPhase === "done" && recToken ? (
            <>
              <dl className="kv">
                <dt>Dispositivo</dt>
                <dd>
                  {device.name} <span className="mono cell-sub">{recToken.deviceUid}</span>
                </dd>
                <dt>Validade</dt>
                <dd>{formatDate(recToken.expiresAt)} â€” 24 horas a partir da geracao</dd>
                <dt>Uso</dt>
                <dd>Unico â€” nao compartilhe este token</dd>
              </dl>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor="recovery-token-value">Token</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <code
                    id="recovery-token-value"
                    className="mono"
                    style={{ fontSize: 16, letterSpacing: 2, userSelect: "all" }}
                  >
                    {recToken.token}
                  </code>
                </div>
              </div>
              {recError ? <ErrorAlert message={recError} /> : null}
              {recCopied ? <div className="alert info">Token copiado para a area de transferencia.</div> : null}
              <p className="subtle" style={{ marginTop: 8 }}>
                Copie o token agora e guarde em local seguro. Apos fechar, ele nao sera mais exibido.
              </p>
            </>
          ) : null}

          {recPhase === "error" ? (
            <>
              <ErrorAlert message={recError ?? "Falha ao gerar o token."} />
              {recAmbiguous ? (
                <div className="alert info" style={{ marginTop: 8 }}>
                  Nenhum novo token sera gerado automaticamente. Verifique a situacao antes de tentar novamente.
                </div>
              ) : null}
            </>
          ) : null}
        </Modal>
      ) : null}

      <Card title="Remover dispositivo">
        <p className="subtle" style={{ marginTop: 0 }}>
          Apaga deste console o inventario, historico, localizacoes, cercas e sessoes remotas de
          <strong> {device.name}</strong>. O agente instalado no computador <strong>nao</strong> e desinstalado â€”
          se ele continuar rodando e for reinstalado, volta a aparecer aqui.
        </p>
        <button className="btn danger" type="button" onClick={() => setConfirmRemove(true)}>
          Remover dispositivo
        </button>
      </Card>

      {confirmRemove ? (
        <Modal
          title="Remover dispositivo"
          onClose={() => setConfirmRemove(false)}
          footer={
            <>
              <button className="btn" type="button" onClick={() => setConfirmRemove(false)} disabled={removing}>
                Cancelar
              </button>
              <button className="btn danger" type="button" onClick={() => void removeDevice()} disabled={removing}>
                {removing ? "Removendo..." : "Remover definitivamente"}
              </button>
            </>
          }
        >
          <p>
            Remover <strong>{device.name}</strong> ({device.uid})? Esta acao apaga o historico e nao pode ser
            desfeita.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
